// File: src/components/pages/PrivacyPolicy.jsx
// Publicly accessible — no authentication required

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ChevronLeft, Mail, Globe } from 'lucide-react';
import '../../styles/unified.css';

const Section = ({ number, title, children }) => (
  <section style={{ marginBottom: '2rem' }}>
    <h2 style={{
      fontSize: '1.1rem', fontWeight: 700, color: '#111827',
      display: 'flex', alignItems: 'center', gap: '0.6rem',
      marginBottom: '0.85rem', paddingBottom: '0.5rem',
      borderBottom: '1px solid #e5e7eb'
    }}>
      <span style={{
        background: 'linear-gradient(135deg, #667eea, #764ba2)',
        color: '#fff', borderRadius: '50%',
        width: '1.6rem', height: '1.6rem', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.75rem', fontWeight: 700
      }}>{number}</span>
      {title}
    </h2>
    <div style={{ color: '#374151', fontSize: '0.9rem', lineHeight: 1.75 }}>
      {children}
    </div>
  </section>
);

const BulletList = ({ items }) => (
  <ul style={{ margin: '0.5rem 0 0.5rem 1.25rem', padding: 0 }}>
    {items.map((item, i) => (
      <li key={i} style={{ marginBottom: '0.3rem' }}>{item}</li>
    ))}
  </ul>
);

const SubSection = ({ title, children }) => (
  <div style={{ marginBottom: '1rem' }}>
    <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1f2937', marginBottom: '0.4rem' }}>{title}</h3>
    {children}
  </div>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* ── Header banner ── */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '2.5rem 2rem 2rem',
        color: '#fff'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
              background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '0.4rem', padding: '0.35rem 0.8rem',
              color: '#fff', fontSize: '0.82rem', cursor: 'pointer',
              marginBottom: '1.25rem'
            }}
          >
            <ChevronLeft size={14} /> Back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '0.5rem' }}>
            <Shield size={32} color="rgba(255,255,255,0.9)" />
            <h1 style={{ margin: 0, fontSize: '1.9rem', fontWeight: 700 }}>Privacy Policy</h1>
          </div>
          <p style={{ margin: 0, opacity: 0.85, fontSize: '0.9rem' }}>
            BipzApp &mdash; Effective Date: 30 April 2026
          </p>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
        <div style={{
          background: '#fff', borderRadius: '0.85rem',
          border: '1px solid #e5e7eb', padding: '2rem 2.5rem'
        }}>

          {/* Intro paragraph */}
          <p style={{ color: '#4b5563', fontSize: '0.9rem', lineHeight: 1.75, marginBottom: '2rem' }}>
            BipzApp ("we", "us", or "our") operates the BipzApp mobile application (the "App"),
            a driving school management platform designed for instructors to manage bookings,
            students, and payments. This Privacy Policy explains what information we collect, how
            we use it, who we share it with, and your rights regarding your personal data. It
            applies to all users of the BipzApp mobile application on iOS and Android.
            By using the App you agree to the collection and use of information in accordance
            with this policy. If you do not agree, please do not use the App.
          </p>

          {/* Section 1 — Introduction (covered by intro above, skip numbering to 2) */}

          <Section number="1" title="Who We Are (Data Controller)">
            <p style={{ margin: '0 0 0.5rem' }}>
              <strong>Company:</strong> BipzApp<br />
              <strong>Contact:</strong>{' '}
              <a href="mailto:nbipinraj@bipzapp.com" style={{ color: '#667eea' }}>nbipinraj@bipzapp.com</a><br />
              <strong>Website:</strong>{' '}
              <a href="https://bipzapp.com" target="_blank" rel="noreferrer" style={{ color: '#667eea' }}>https://bipzapp.com</a>
            </p>
            <p style={{ margin: 0 }}>
              If you have any questions about this Privacy Policy or how we handle your data,
              please contact us at the email address above.
            </p>
          </Section>

          <Section number="2" title="Information We Collect">
            <SubSection title="2.1 Account Information">
              <p style={{ margin: '0 0 0.4rem' }}>When you register or log in via Auth0 (our authentication provider), we collect:</p>
              <BulletList items={[
                'Full name', 'Email address',
                'Authentication credentials (managed securely by Auth0)',
                'Auth0 user identifier'
              ]} />
            </SubSection>
            <SubSection title="2.2 Instructor Profile Information">
              <p style={{ margin: '0 0 0.4rem' }}>Instructors provide the following when configuring their account:</p>
              <BulletList items={[
                'Phone number',
                'Lesson pricing and duration preferences',
                'Working hours and availability schedule',
                'Discount configurations'
              ]} />
            </SubSection>
            <SubSection title="2.3 Student Information">
              <p style={{ margin: '0 0 0.4rem' }}>Instructors enter the following information about their students:</p>
              <BulletList items={[
                'Full name', 'Email address', 'Phone number', 'Date of birth',
                'Driving licence number', 'Vehicle preference (automatic / manual)',
                'Pickup and drop-off addresses and postcodes',
                'Student progress records and lesson notes'
              ]} />
            </SubSection>
            <SubSection title="2.4 Booking and Payment Information">
              <p style={{ margin: '0 0 0.4rem' }}>The App records:</p>
              <BulletList items={[
                'Lesson booking dates, times, and durations',
                'Booking status (confirmed, cancelling, cancelled)',
                'Payment amounts, methods, package types, and discount details',
                'Payment status (claimed, confirmed, rejected)',
                'Number of lessons paid for and completed'
              ]} />
            </SubSection>
            <SubSection title="2.5 Device and Usage Information">
              <p style={{ margin: '0 0 0.4rem' }}>We may collect limited technical information including:</p>
              <BulletList items={[
                'Device type and operating system version',
                'App version',
                'Error logs for debugging purposes'
              ]} />
              <p style={{ margin: '0.5rem 0 0' }}>
                We do not use advertising identifiers or third-party analytics SDKs.
              </p>
            </SubSection>
          </Section>

          <Section number="3" title="How We Use Your Information">
            <p style={{ margin: '0 0 0.4rem' }}>We use the information collected for the following purposes:</p>
            <BulletList items={[
              'Providing and operating the BipzApp service for instructors',
              'Authenticating users securely via Auth0',
              'Displaying booking schedules, student records, and payment history',
              'Sending invitation emails to prospective students',
              'Processing and recording lesson payments',
              'Generating booking confirmations and cancellation requests',
              'Tracking student progress through lesson levels',
              'Improving the App based on error logs and usage patterns',
              'Complying with legal obligations'
            ]} />
          </Section>

          <Section number="4" title="Legal Basis for Processing (UK GDPR)">
            <p style={{ margin: '0 0 0.4rem' }}>For users in the United Kingdom, our legal bases for processing personal data are:</p>
            <BulletList items={[
              'Contract performance — processing necessary to provide the App services you have signed up for',
              'Legitimate interests — operating and improving the App, preventing fraud',
              'Legal obligation — complying with applicable laws and regulations',
              'Consent — where we explicitly ask for and you provide consent (e.g. invitation emails)'
            ]} />
          </Section>

          <Section number="5" title="Who We Share Your Data With">
            <p style={{ margin: '0 0 0.75rem' }}>
              We do not sell your personal data. We share data only with the following trusted
              service providers who process data on our behalf:
            </p>
            <SubSection title="5.1 Auth0 (Okta)">
              <p style={{ margin: 0 }}>
                Authentication and identity management. Auth0 stores and manages login credentials
                securely. Auth0 is SOC 2 Type II certified.
              </p>
            </SubSection>
            <SubSection title="5.2 Amazon Web Services (AWS)">
              <p style={{ margin: 0 }}>
                Our backend infrastructure runs on AWS in the eu-west-2 (London) region.
              </p>
            </SubSection>
            <SubSection title="5.3 Legal Disclosure">
              <p style={{ margin: 0 }}>
                We may disclose your information if required to do so by law or in response to
                valid requests by public authorities (e.g. a court order or government agency).
              </p>
            </SubSection>
          </Section>

          <Section number="6" title="Data Retention">
            <p style={{ margin: '0 0 0.4rem' }}>We retain your personal data for as long as your account is active or as needed to provide the service. Specifically:</p>
            <BulletList items={[
              'Account data — retained while your account is active',
              'Booking history — retained for 7 years for business record-keeping purposes',
              'Student invitation tokens — deleted after 30 days if unused'
            ]} />
            <p style={{ margin: '0.5rem 0 0' }}>
              When you request deletion of your account, we will delete or anonymise your personal
              data within 30 days, except where retention is required by law.
            </p>
          </Section>

          <Section number="7" title="Data Security">
            <p style={{ margin: '0 0 0.4rem' }}>We implement appropriate technical and organisational measures to protect your personal data:</p>
            <BulletList items={[
              'All data in transit is encrypted using TLS (HTTPS)',
              'All data at rest in DB is encrypted using AES-256',
              'Authentication tokens are stored in device Secure Storage (iOS Keychain / Android Keystore equivalent)',
              'Auth0 handles password hashing and brute-force protection',
              'IAM roles enforce least-privilege access to all backend services'
            ]} />
            <p style={{ margin: '0.5rem 0 0' }}>
              No method of transmission over the internet or electronic storage is 100% secure.
              We strive to use commercially acceptable means to protect your data but cannot
              guarantee absolute security.
            </p>
          </Section>

          <Section number="8" title="Your Rights">
            <p style={{ margin: '0 0 0.4rem' }}>Under UK GDPR, you have the following rights regarding your personal data:</p>
            <BulletList items={[
              'Right of access — request a copy of the personal data we hold about you',
              'Right to rectification — request correction of inaccurate or incomplete data',
              'Right to erasure — request deletion of your personal data ("right to be forgotten")',
              'Right to restrict processing — request that we limit how we use your data',
              'Right to data portability — receive your data in a structured, machine-readable format',
              'Right to object — object to processing based on legitimate interests',
              'Right to withdraw consent — where processing is based on consent, you may withdraw it at any time'
            ]} />
            <p style={{ margin: '0.5rem 0 0' }}>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:nbipinraj@bipzapp.com" style={{ color: '#667eea' }}>nbipinraj@bipzapp.com</a>.
            </p>
          </Section>

          <Section number="9" title="Children's Privacy">
            <p style={{ margin: '0 0 0.5rem' }}>
              The App is intended for use by adult driving instructors and adult student drivers.
              We do not knowingly collect personal data from children under the age of 17 without
              verifiable parental consent.
            </p>
            <p style={{ margin: 0 }}>
              If you believe we have inadvertently collected data from a minor, please contact us
              immediately and we will take steps to delete such data.
            </p>
          </Section>

          <Section number="10" title="International Data Transfers">
            <p style={{ margin: '0 0 0.5rem' }}>
              All primary data processing takes place within the United Kingdom using AWS (London).
              Some data may be processed by our sub-processors (Auth0, AWS global services) in
              the United States or other countries.
            </p>
            <p style={{ margin: 0 }}>
              Where data is transferred outside the UK, we ensure appropriate safeguards are in
              place, including Standard Contractual Clauses (SCCs) or equivalent protections
              recognised by the ICO.
            </p>
          </Section>

          <Section number="11" title="Third-Party Links and Services">
            <p style={{ margin: 0 }}>
              We are not responsible for the privacy practices of third-party sites and encourage
              you to review their privacy policies.
            </p>
          </Section>

          <Section number="12" title="Changes to This Privacy Policy">
            <p style={{ margin: '0 0 0.5rem' }}>
              We may update this Privacy Policy from time to time. When we do, we will update the
              "Effective Date" at the top of this document and notify you through the App or by
              email if the changes are material.
            </p>
            <p style={{ margin: 0 }}>
              Your continued use of the App after changes are posted constitutes your acceptance
              of the updated policy.
            </p>
          </Section>

          <Section number="13" title="Contact Us">
            <p style={{ margin: '0 0 0.75rem' }}>
              If you have any questions, concerns, or requests regarding this Privacy Policy or
              our data practices, please contact us:
            </p>
            <div style={{
              background: '#f8fafc', borderRadius: '0.6rem',
              border: '1px solid #e5e7eb', padding: '1rem 1.25rem',
              display: 'flex', flexDirection: 'column', gap: '0.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <Mail size={15} color="#667eea" />
                <a href="mailto:nbipinraj@bipzapp.com" style={{ color: '#667eea', fontWeight: 500 }}>
                  nbipinraj@bipzapp.com
                </a>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <Globe size={15} color="#667eea" />
                <a href="https://bipzapp.com" target="_blank" rel="noreferrer" style={{ color: '#667eea', fontWeight: 500 }}>
                  https://bipzapp.com
                </a>
              </div>
            </div>
          </Section>

        </div>

        {/* Footer note */}
        <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '0.78rem', marginTop: '1.5rem' }}>
          &copy; {new Date().getFullYear()} BipzApp. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;